import { types, Instance, flow } from 'mobx-state-tree';
import api from '../services/api';

// Import MST store models
import { AuthStore } from './AuthStore';
import { UserStore } from './UserStore';
import { MindmapStore } from './MindmapStore';
import { OrganizationStore } from './OrganizationStore';
import { ThemeStore } from './ThemeStore';
import { FeatureFlagsStore } from './FeatureFlagsStore';
import { MindmapViewportStore } from './MindmapViewportStore';
import { LayoutStore } from './LayoutStore';
import { LogsStore } from './LogsStore';
import { WorkspaceRealtimeStore } from './WorkspaceRealtimeStore';
import { OnlinePresenceStore } from './OnlinePresenceStore';
import { MindmapCursorStore } from './MindmapCursorStore';
import { ChatStoreMST } from './ChatStoreMST';
import { TicketStoreMST } from './TicketStoreMST';
import { AppPreferencesStore } from './AppPreferencesStore';

/**
 * UNIFIED APP STORE
 * Single MST root store containing all application state
 * - No persistence (everything loaded fresh from API)
 * - Token verification happens before any initialization
 * - All data loaded from workspace endpoint after auth
 * - UI preferences (sidebar state, etc.) are persisted to localStorage
 */
const AppStore = types
  .model('AppStore', {
    // Authentication & User
    auth: AuthStore,
    user: UserStore,
    
    // Core Business Logic
    organization: OrganizationStore,
    mindmap: MindmapStore,
    chat: ChatStoreMST,
    tickets: TicketStoreMST,
    
    // Realtime & Presence
    workspaceRealtime: WorkspaceRealtimeStore,
    onlinePresence: OnlinePresenceStore,
    mindmapCursor: MindmapCursorStore,
    
    // UI & System
    theme: ThemeStore,
    layout: LayoutStore,
    mindmapViewport: MindmapViewportStore,
    featureFlags: FeatureFlagsStore,
    logs: LogsStore,
    preferences: AppPreferencesStore,
    
    // App State
    isInitialized: types.optional(types.boolean, false),
    isInitializing: types.optional(types.boolean, false),
    initializationError: types.maybeNull(types.string),
  })
  .views(self => ({
    get isAuthenticated() {
      return self.auth.isReallyAuthenticated;
    },
    
    get currentUser() {
      return self.auth.user;
    },
    
    get currentOrganization() {
      return self.user.currentOrganization;
    },
    
    get isReady() {
      return self.isInitialized && !self.isInitializing && !self.initializationError;
    }
  }))
  .actions(self => ({
    // UNIFIED INITIALIZATION - Handles everything in proper order
    initializeApp: flow(function* (orgSlug?: string) {
      if (self.isInitializing) {
        console.warn('[AppStore] Already initializing, skipping...');
        return;
      }
      
      self.isInitializing = true;
      self.initializationError = undefined;
      
      try {
        console.log('🚀 [AppStore] Starting unified app initialization...');
        
        // Step 0: Load UI preferences from localStorage and sync with LayoutStore
        console.log('[AppStore] Step 0: Loading UI preferences...');
        self.preferences.loadFromStorage();
        self.layout.loadSidebarStateFromPreferences(self.preferences.isSidebarCollapsed);
        console.log('[AppStore] Step 0 COMPLETE - preferences loaded and synced');
        
        // Step 1: Check if authenticated via Stytch
        // NOTE: Stytch session is managed by StytchSessionSync component
        // We just check if auth is already set (from Stytch JWT cookies)
        console.log('[AppStore] Step 1: Checking auth state (Stytch-managed)...');
        const authSuccess = self.auth.isAuthenticated;
        console.log('[AppStore] Step 1 COMPLETE - authSuccess:', authSuccess);
        
        if (!authSuccess) {
          console.log('[AppStore] Not authenticated, completing initialization for login flow');
          self.isInitialized = true;
          return false; // Return false to indicate auth failed
        }
        
        // Step 2: Get org slug from auth result or parameter
        console.log('[AppStore] Step 2: Resolving organization...');
        
        // Prefer using slug for better URLs and consistency
        let workspaceIdentifier = orgSlug || self.auth.organization?.slug;
        console.log('[AppStore] Step 2a: workspaceIdentifier (slug):', workspaceIdentifier);
        
        // Fallback to loading by slug if we only have the ID
        if (!workspaceIdentifier && self.auth.user?.organizationId) {
          const currentOrgId = self.auth.user.organizationId;
          console.log('[AppStore] Step 2b: Have org ID but no slug, loading org to get slug:', currentOrgId);
          
          // We need to get the slug - but for now, use the ID
          workspaceIdentifier = currentOrgId;
          console.log('[AppStore] Step 2c: Using org ID as fallback:', workspaceIdentifier);
        }
        
        if (!workspaceIdentifier) {
          console.warn('[AppStore] No organization slug or ID found, user needs to select org');
          self.isInitialized = true;
          return false;
        }
        console.log('[AppStore] Step 2 COMPLETE - workspaceIdentifier:', workspaceIdentifier);
        
        // Step 3: Load workspace data AND connect WebSocket in parallel
        console.log('[AppStore] Step 3: Loading workspace & connecting WebSocket...');
        
        const currentOrgId = self.organization?.organizationId;
        console.log(`[AppStore] Current org ID BEFORE workspace load: ${currentOrgId}`);
        
        // Load workspace first to get org ID
        yield self.loadWorkspace(workspaceIdentifier);
        
        // THEN connect WebSocket with the loaded org ID
        const loadedOrgId = self.organization?.organizationId;
        console.log(`[AppStore] Loaded org ID: ${loadedOrgId}`);
        
        if (loadedOrgId) {
          yield self.workspaceRealtime.connectToWorkspace(loadedOrgId);
        } else {
          console.error('[AppStore] ❌ Cannot connect WebSocket - no org ID after workspace load!');
        }
        
        console.log('✅ [AppStore] Workspace loaded & WebSocket connected');
        
        // Step 4: Initialize chat system
        console.log('[AppStore] Step 4: Initializing chat system...');
        yield self.chat.initialize();
        
        // Step 5: Load tickets and presence history in parallel
        console.log('[AppStore] Step 5: Loading tickets & presence history...');
        yield Promise.all([
          self.tickets.fetchTickets(),
          self.onlinePresence.fetchPresenceHistory(30),
        ]);
        
        console.log('✅ [AppStore] Tickets & presence loaded');
        
        self.isInitialized = true;
        console.log('✅ [AppStore] Complete unified initialization successful');
        return true;
        
      } catch (error) {
        console.error('❌ [AppStore] Unified initialization failed:', error);
        self.initializationError = error.message || 'Initialization failed';
        return false;
      } finally {
        self.isInitializing = false;
      }
    }),
    
    // Load workspace data (replaces old RootStore.loadWorkspace)
    loadWorkspace: flow(function* (orgId: string) {
      if (!self.isAuthenticated) {
        console.warn('[AppStore] Not authenticated, skipping workspace load');
        return;
      }
      
      try {
        const url = `/workspace/${orgId}`;
        
        console.log(`📦 [AppStore] Loading workspace from: ${url}`);
        
        const response = yield api.get(url);
        
        const data = response.data || {};
        console.log('📥 [AppStore] Workspace data received:', Object.keys(data));
        
        // Hydrate organization store
        if (data.organization) {
          self.organization.setOrganizationId(data.organization.id);
          self.organization.setOrganizationName(data.organization.name);
        }
        
        // Load organization members from workspace data
        if (data.members && Array.isArray(data.members)) {
          console.log(`[AppStore] Loading ${data.members.length} organization members from workspace...`);
          self.user.users.clear();
          data.members.forEach((member: any) => {
            // Skip soft-deleted members
            if (member.isDeleted) {
              console.log(`⏭️ Skipping deleted member: ${member.email}`);
              return;
            }
            
            self.user.users.push({
              id: member.id,
              email: member.email,
              name: member.name,
              firstName: member.firstName || '',
              lastName: member.lastName || '',
              initials: member.initials || '',
              avatar: member.avatarUrl,
              avatarUrl: member.avatarUrl,
              organizationId: data.organization?.id,
              role: member.role || 'member',
              status: member.status || 'active',
              isActive: member.status === 'active',
              lastSeen: undefined,
            });
          });
          console.log(`✅ [AppStore] Loaded ${data.members.length} members from workspace`);
        } else {
          console.warn('[AppStore] No members data in workspace response, loading separately...');
          yield self.user.loadOrgUsers();
        }
        
        // Hydrate mindmaps
        if (data.mindmaps) {
          self.organization.setMindmaps(data.mindmaps);
        }
        
        // Hydrate folders
        if (data.folders) {
          self.organization.setFolders(data.folders);
        }
        
        // Hydrate chat data from workspace endpoint
        if (data.chat) {
          console.log('📥 [AppStore] Hydrating chat data from workspace...');
          console.log('📊 [AppStore] Chat data keys:', Object.keys(data.chat));
          console.log('📊 [AppStore] Chat folders count:', data.chat.folders?.length || 0);
          console.log('📊 [AppStore] Chat conversations count:', data.chat.conversations?.length || 0);
          
          if (data.chat.conversations) {
            console.log('🔄 [AppStore] Setting conversations...');
            self.chat.setConversations(data.chat.conversations);
            console.log('✅ [AppStore] Conversations set:', self.chat.conversationsArray.length);
          }
          if (data.chat.folders) {
            console.log('🔄 [AppStore] Setting chat folders...');
            self.chat.setFolders(data.chat.folders);
            console.log('✅ [AppStore] Chat folders set:', self.chat.foldersArray.length);
          }
        } else {
          console.warn('⚠️ [AppStore] No chat data in workspace response');
        }
        
        // Hydrate available AI models from workspace endpoint
        if (data.models) {
          console.log('📥 [AppStore] Hydrating AI models from workspace...');
          self.chat.setAvailableModels(data.models);
        }
        
        // Initialize chat system (WebSocket only - data already loaded)
        yield self.chat.initialize();
        
        console.log('✅ [AppStore] Workspace loaded successfully');
        
      } catch (error) {
        console.error('❌ [AppStore] Failed to load workspace:', error);
        if (error.response?.status === 401) {
          console.log('[AppStore] 401 error, clearing auth');
          self.auth.clearAuth();
        }
        throw error;
      }
    }),
    
    // Reset all stores (for logout)
    reset() {
      console.log('🔄 [AppStore] Resetting all stores...');
      
      // Reset all stores to initial state
      self.auth.clearAuth();
      self.user.clearOrganizations();
      self.organization.reset();
      self.mindmap.reset();
      self.chat.reset();
      self.tickets.reset();
      
      // Disconnect workspace WebSocket
      self.workspaceRealtime.disconnectFromWorkspace();
      
      // Reset app state
      self.isInitialized = false;
      self.isInitializing = false;
      self.initializationError = undefined;
      
      console.log('✅ [AppStore] All stores reset');
    },
    
    // Handle authentication changes
    onAuthChange(isAuthenticated: boolean) {
      if (!isAuthenticated) {
        // User logged out, reset everything
        self.reset();
      }
    }
  }));

// Lazy app store instance - only created when needed
let appStoreInstance: IAppStore | null = null;

/**
 * Get or create the app store instance
 * This is lazy - store is only created when first requested
 */
export const getAppStore = (): IAppStore => {
  if (!appStoreInstance) {
    console.log('🏗️ [AppStore] Creating unified app store...');
    appStoreInstance = AppStore.create({
      auth: {},
      user: {},
      organization: {},
      mindmap: {},
      chat: {},
      tickets: {},
      workspaceRealtime: {},
      onlinePresence: {},
      mindmapCursor: {},
      theme: {},
      layout: {},
      mindmapViewport: {},
      featureFlags: {},
      logs: {
        isDatabaseLogsCollapsed: false,
        isDebugLogsCollapsed: false,
        isDebugControlsCollapsed: false,
      },
      preferences: {},
    });
    console.log('✅ [AppStore] Unified app store created successfully');
  }
  return appStoreInstance;
};

/**
 * Destroy the app store instance completely
 * Called on signout to clean up all state
 */
export const destroyAppStore = () => {
  if (appStoreInstance) {
    console.log('💣 [AppStore] Destroying app store...');
    // Clear all data
    appStoreInstance = null;
    console.log('✅ [AppStore] App store destroyed');
  }
};

// Export for backwards compatibility - but this will be lazy now
// Use a Proxy that forwards all property access to the lazy store instance
export const appStore: IAppStore = new Proxy({} as IAppStore, {
  get(target, prop) {
    const store = getAppStore();
    const value = store[prop as keyof IAppStore];
    // If it's a function, bind it to the store instance
    return typeof value === 'function' ? value.bind(store) : value;
  }
});

// Expose on window for debugging and for setupLogging.ts
if (typeof window !== 'undefined') {
  (window as any).__APP_STORE__ = appStore;
}

// Export types
export type IAppStore = Instance<typeof AppStore>;

// Export the store
export { AppStore };
export default appStore;
